/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
*/

import moment from 'moment'
import qs from 'query-string'
import Cookies from 'cookies-js'
import R from 'ramda'

import Request from 'generic/request/Request'
import { getDateTime } from 'utils/date'
import { messages } from 'constants/Header'

import * as headerConstants from 'constants/Header'
import * as prestationConstants from 'constants/Prestation'

import * as applicantActions from 'actions/Applicant'
import * as languageActions from 'actions/Language'
import * as qualificationActions from 'actions/Qualification'
import * as skillActions from 'actions/Skill'
import * as beneficiaryNumberActions from 'actions/BeneficiaryNumber'

import * as constants from 'constants/Prestation'

const PRESTATION_URL = '/api/prestation/'

const PRESTATION_VALIDATION_URL = '/api/prestation/validation/'
const PRESTATION_ADMIN_URL = '/api/prestation/admin/'

const PRESTATION_TYPES_URL = '/api/prestation/type/list/?meta=false&objects_count=false'
const PRESTATION_HISTORY_URL = '/api/prestation/history/workflow/list/'
const PRESTATION_TYPE_URL = '/api/prestation/type/'
const SEX_LIST_URL = '/api/sex/list/?meta=false&objects_count=false'
const NATIONALITIES_URL = '/api/nationality/list/?meta=false&objects_count=false'
const PRESTATION_WORKFLOW_TYPES_URL = '/api/prestation/workflow/list/?meta=false&objects_count=false'
const MATCHING_URL = '/api/matching/list/'
const CONNECTED_USER_URL = '/api/connected/user/'
const SIMILAR_PRESTATION_URL = '/api/prestation/warning/double/list/'
const DELIVERY_NOTE_URL = '/api/prestation/delivery/note/'
const PRESTATION_CONNECTED_USER_LIST_URL = '/api/prestation/connected/user/list/'
const WORKFLOW_PRESTATION_URL = '/api/prestation/workflow/'
const PRESTATION_MODIFICATION_URL = '/api/prestation/correction/'
const PRESTATION_EVALUATION_URL = '/api/prestation/evaluation/'
const PRESTATION_VALIDATION_DISTANCE_URL = '/api/prestation/validation/distance/'
const REPORT_URLS = {
  [prestationConstants.PRESTATION_REPORT]: '/api/prestation/report/list/',
  [prestationConstants.PRESTATION_REPORT_PHOTO]: '/api/prestation/report/list/',
  [prestationConstants.PRESTATION_REPORT_CREATION_DATE]: '/api/prestation/report/list/',
  [prestationConstants.PRESTATION_REPORT_VALIDATION_DATE]: '/api/prestation/validation/report/list/',
  [prestationConstants.PRESTATION_REPORT_GENERAL]: '/api/prestation/report/general/list/',
  [prestationConstants.PRESTATION_REPORT_STATISTIC]: '/api/prestation/report/statistic/list/',
  [prestationConstants.LANGUAGE_PRESTATION_REPORT]: '/api/prestation/language/report/list/',
  [prestationConstants.INTERPRETER_PRESTATION_REPORT]: '/api/prestation/interpreter/report/list/',
  [prestationConstants.APPLICANT_PRESTATION_REPORT]: '/api/prestation/applicant/report/list/',
  [prestationConstants.PAYING_CUSTOMER_PRESTATION_REPORT]: '/api/prestation/paying/customer/report/list/',
}

export const ADDRESSES_URL = '/api/prestation/address/list/'

let prestationController
let selectPrestationController
let getPrestationUidController

export function getPrestations (params) {
  if (prestationController) {
    prestationController.abort()
  }
  if (selectPrestationController) {
    selectPrestationController.abort()
  }
  if (getPrestationUidController) {
    getPrestationUidController.abort()
  }

  prestationController = new window.AbortController()

  const query = {
    ...params,
    meta: false,
  }

  return Request.getRequest(
    PRESTATION_CONNECTED_USER_LIST_URL + '?' + qs.stringify(query),
    {signal: prestationController.signal},
  )
}

export function getPrestationUid (uid, cancellable=true) {
  if (getPrestationUidController) getPrestationUidController.abort()
  getPrestationUidController = cancellable ? new window.AbortController() : undefined

  return Request.getRequest(PRESTATION_URL + (uid ? uid + '/' : ''), {signal: R.path(['signal'], getPrestationUidController)})
}

export function postPrestationValidation(prestation) {
  return function (dispatch) {
    return Request.postRequest(PRESTATION_VALIDATION_URL + (prestation.uid ? prestation.uid + '/' : ''), prestation)
  }
}

export function postPrestation (prestation) {
  return Request.postRequest(PRESTATION_URL + (prestation.uid ? prestation.uid + '/' : ''), prestation)
}

const getList = (url, key, dispatch) => {
  return Request.getRequest(url)
    .then((results) => {
      dispatch({
        type: prestationConstants.SET_DATA,
        data: results.data.objects.objects,
        key: key
      })
    })
}

export function loadPrestation (uid) {
  let prestationObj = null

  return Request.getRequest(PRESTATION_URL + (uid ? uid + '/' : ''))
    .then((result) => {
      prestationObj = result.data.object
      prestationObj.confirmAuto = false

      if (uid) {
        prestationObj.ordered_date = moment(result.data.object.ordered_start_time).format('YYYY-MM-DD')
        prestationObj.ordered_start_time = moment(result.data.object.ordered_start_time, 'YYYY-MM-DD HH:mm:ss').toDate()
        prestationObj.ordered_end_time = moment(result.data.object.ordered_end_time, 'YYYY-MM-DD HH:mm:ss').toDate()

        if (result.data.object.delivered_start_time) {
          prestationObj.delivered_date = moment(result.data.object.delivered_start_time).format('YYYY-MM-DD')
          prestationObj.delivered_start_time = moment(result.data.object.delivered_start_time, 'YYYY-MM-DD HH:mm:ss').toDate()
          prestationObj.delivered_end_time = moment(result.data.object.delivered_end_time, 'YYYY-MM-DD HH:mm:ss').toDate()
        }

        prestationObj.validation = undefined
      } else {
        return getPrestationType().then((prestationType) => {
          prestationObj.prestation_type = prestationType
        })
      }
    })
    .then(() => {
      const beneficiaryNumbers = prestationObj.beneficiary_numbers

      if (beneficiaryNumbers.length === 0) {
        return Request.getRequest(beneficiaryNumberActions.BENEFICIARY_NUMBER_URL)
      }

      return beneficiaryNumbers
    })
    .then((results) => {
      let beneficiaryNumbers = results

      if (results.data) {
        results.data.object.beneficiary = null
        beneficiaryNumbers = [results.data.object]
      }

      prestationObj.beneficiary_numbers = beneficiaryNumbers

      return Request.getRequest(CONNECTED_USER_URL)
    })
    .then((user) => {
      const role = user.data.object.roles[0].uid

      return applicantActions.getApplicant(role)
    })
    .then((applicant) => {
      if (prestationObj.applicant &&
          prestationObj.applicant.entities.length === 0) {
        prestationObj.applicant = applicant
      }

      if (!prestationObj.applicant_entity.uid) {
        prestationObj.applicant_entity = applicant.entities[0]
      }

      if (!prestationObj.applicant_identity) {
        prestationObj.applicant_identity = applicant.identities[0]
      }

      if (prestationObj.address &&
          !prestationObj.address.address) {
        prestationObj.address = applicant.entities[0].address
      }

      if (!prestationObj.paying_customer.uid) {
        if (applicant.default_paying_customer) {
          prestationObj.paying_customer = applicant.default_paying_customer
        }
      }

      return prestationObj
    })
}

export function loadInterpreters (params) {
  const query = {
    ...params,
    objects_count: false,
  }

  return Request.getRequest(MATCHING_URL + '?' + qs.stringify(query))
}

export function getMatchingInterpreters (prestation, address, user=undefined) {
  const startTime = getDateTime(moment(prestation.ordered_date, 'YYYY-MM-DD'), moment(prestation.ordered_start_time, 'HH:mm'))
  let endTime = getDateTime(moment(prestation.ordered_date, 'YYYY-MM-DD'), moment(prestation.ordered_end_time, 'HH:mm'))

  if (endTime <= startTime) {
    endTime = moment(endTime, 'YYYY-MM-DD HH:mm').add(1, 'days').format('YYYY-MM-DD HH:mm:ss')
  }

  return loadInterpreters({
    prestation_uid: R.path(['uid'], prestation) || undefined,
    start_time: startTime,
    end_time: endTime,
    preferred_sex: R.path(['preferred_sex'], prestation) ? R.path(['preferred_sex', 'code'], prestation) : undefined,
    preferred_nationality_origin: R.path(['preferred_nationality_origin', 'uid'], prestation) || undefined,
    prestation_type: R.path(['prestation_type', 'code'], prestation),
    address_uid: R.path(['address', 'uid'], prestation) || undefined,
    address: address || undefined,
    interpreter: prestation.choosenInterpreter || undefined,
    user: user,
    languages: R.length(R.path(['languages'], prestation))
      ? R.path(['languages', 0, 'uid'], prestation)
      : undefined,
    preferred_qualification: R.length(R.path(['preferred_qualifications'], prestation))
      ? R.path(['preferred_qualifications', 0, 'code'], prestation)
      : undefined,
    preferred_skills: R.length(R.path(['preferred_skills'], prestation))
      ? R.path(['preferred_skills', 0, 'uid'], prestation)
      : undefined,
  })
}

export function similarPrestation (date, prestation) {
  const startTime = getDateTime(moment(date, 'YYYY-MM-DD'), moment(prestation.ordered_start_time, 'HH:mm'))
  let endTime = getDateTime(moment(date, 'YYYY-MM-DD'), moment(prestation.ordered_end_time, 'HH:mm'))

  if (endTime <= startTime) {
    endTime = moment(endTime).add(1, 'days')
    endTime = endTime.format('YYYY-MM-DD HH:mm:ss')
  }

  const params = {
    meta: false,
    objects_count: false,
    start_time: startTime,
    end_time: endTime,
    languages: R.path(['languages', 0, 'uid'], prestation),
    applicant: R.path(['applicant', 'uid'], prestation),
  }

  return Request.getRequest(SIMILAR_PRESTATION_URL + '?' + qs.stringify(params))
}

const getPrestationType = () => {
  return Request.getRequest(PRESTATION_TYPE_URL + prestationConstants.DEFAULT_PRESTATION_TYPE + '/')
    .then((result) => {
      return result.data.object
    })
}

export function getPrestation (uid) {
  return function (dispatch, getState) {
    return loadPrestation(uid)
      .then((prestation) => {
        return {
          ...prestation,
          interpreters: []
        }
      })
  }
}

export function postPrestationAdmin(data) {
  return Request.postRequest(PRESTATION_ADMIN_URL + data.uid + '/', data)
}

export function cancelPrestation (prestation, comment, billing) {
  return function (dispatch, getState) {
    function executeCancellation (prestationUid, comment) {
      const data = {
        comment: comment,
        name: prestationConstants.WORKFLOW_NAME,
        step_name: prestationConstants.CANCELLED_PRESTATION_STATUS
      }

      return Request.postRequest(WORKFLOW_PRESTATION_URL + '?target=' + prestationUid, data)
    }

    function updatePrestationList (prestation) {
      let existingPrestations = [...getState().ApplicantPrestation.prestations]

      const index = existingPrestations.findIndex((p1) => p1.uid === prestation.uid)
      if (index > -1) {
        existingPrestations[index] = prestation
      }

      dispatch({
        type: prestationConstants.SELECT_PRESTATIONS,
        prestations: existingPrestations
      })

      dispatch({
        type: prestationConstants.SELECT_PRESTATION,
        prestation: prestation
      })

      return prestation
    }

    return executeCancellation(prestation.uid, comment)
      .then(() => {
        return Request.getRequest(PRESTATION_URL + prestation.uid + '/')
      })
      .then((result) => {
        return updatePrestationList(result.data.object)
      })
  }
}

export function setNotification (text, notificationType) {
  return function (dispatch) {
    dispatch({
      type: headerConstants.SET_NOTIFICATION,
      text: text,
      notifType: notificationType,
    })
  }
}

export function setData (key, value) {
  return function (dispatch) {
    dispatch({type: prestationConstants.SET_DATA, data: value, key: key})
  }
}

export function selectPrestation (prestation) {
  if (selectPrestationController) {
    selectPrestationController.abort()
  }

  selectPrestationController = new window.AbortController()

  return function (dispatch) {
    return Request.getRequest(PRESTATION_URL + prestation.uid + '/', {signal: selectPrestationController.signal})
      .then((prestation) => {
        dispatch({
          type: prestationConstants.SELECT_PRESTATION,
          prestation: prestation.data.object
        })
      })
  }
}

export function getPrestationHistories (prestation) {
  const query = {
    meta: false,
    objects_count: false,
    name: 'prestation_workflow',
    target_id: prestation.id,
    sort: 'datetime'
  }

  return Request.getRequest(PRESTATION_HISTORY_URL + '?' + qs.stringify(query))
    .then((results) => {
      return results.data.objects.objects
    })
}

export function loadStaticData () {
  return function (dispatch, getState) {
    if (getState().ApplicantPrestation.staticLoaded) {
      return Promise.resolve()
    }

    return Promise.all([
      getList(languageActions.SIMPLE_LANGUAGES_URL, 'languages', dispatch),
      getList(skillActions.SKILLS_URL, 'skills', dispatch),
      getList(qualificationActions.QUALIFICATIONS_URL, 'qualifications', dispatch),
      getList(beneficiaryNumberActions.BENEFICIARY_NUMBER_TYPES_URL, 'beneficiaryNumberTypes', dispatch),
      getList(NATIONALITIES_URL, 'origines', dispatch),
      getList(PRESTATION_TYPES_URL, 'prestationTypes', dispatch),
      getList(SEX_LIST_URL, 'sexes', dispatch)
    ])
      .then(() => {
        dispatch({type: prestationConstants.SET_STATIC_LOADED})

        return Request.getRequest(PRESTATION_WORKFLOW_TYPES_URL)
      })
      .then((result) => {
        if (result.data.objects.objects.length > 0) {
          dispatch({
            type: prestationConstants.SELECT_WORKFLOW_STEPS,
            workflow_steps: result.data.objects.objects[0].stages[0].steps
          })
        }
      })
  }
}

export function getFiltering (data) {
  const dateAfter = data.after
    ? moment(data.after).set({hours: 0, minutes: 0, seconds: 0}).format('YYYY-MM-DD HH:mm:ss')
    : undefined

  const dateBefore = data.before
    ? moment(data.before).set({hours: 23, minutes: 59, seconds: 59}).format('YYYY-MM-DD HH:mm:ss')
    : undefined

  return {
    sort: '-ordered_start_time',
    meta: false,
    objects_count: false,
    id: R.path(['uid'], data),
    invoice_aba_code: R.path(['invoice_aba_code'], data),
    date_gte: dateAfter || R.path(['date_gte'], data),
    date_lte: dateBefore || R.path(['date_lte'], data),
    invoice_date: R.path(['invoice_date'], data)
      ? moment(data.invoice_date).format('YYYY-MM-DD')
      : undefined,
    interpreters_in: R.length(R.path(['interpreters'], data))
      ? data.interpreters.map(interpreter => interpreter.uid).join()
      : undefined,
    paying_customers_in: R.length(R.path(['payingCustomers'], data))
      ? data.payingCustomers.map(payingCustomer => payingCustomer.uid).join()
      : undefined,
    applicant_entities_in: R.length(R.path(['applicant_entities'], data))
      ? data.applicant_entities.map(applicantEntity => applicantEntity.uid).join()
      : undefined,
    applicant_identities_in: R.length(R.path(['applicant_identities'], data))
      ? data.applicant_identities.map(applicantIdentity => applicantIdentity.uid).join()
      : undefined,
    consumer_entities_in: R.length(R.path(['consumer_entities'], data))
      ? data.consumer_entities.map(consumerEntity => consumerEntity.uid).join()
      : undefined,
    consumer_identities_in: R.length(R.path(['consumer_identities'], data))
      ? data.consumer_identities.map(consumerIdentity => consumerIdentity.first_name + ' ' + consumerIdentity.last_name).join()
      : undefined,
    beneficiary_number: R.path(['beneficiary_number'], data),
    address_in: R.path(['address'], data),
    invitations_interpreter_uid_in: R.length(R.path(['invited_interpreters'], data))
      ? data.invited_interpreters.map(interpreter => interpreter.uid).join()
      : undefined,
    languages_in: R.length(R.path(['languages'], data))
      ? data.languages.join()
      : undefined,
    preferred_nationality_origin_in: R.length(R.path(['origines'], data))
      ? data.origines.join()
      : undefined,
    prestation_type_in: R.length(R.path(['prestationTypes'], data))
      ? data.prestationTypes.join()
      : undefined,
    wkf_step_in: R.length(R.path(['workflowSteps'], data))
      ? data.workflowSteps.join()
      : undefined,
    wkf_current: R.length(R.path(['workflowSteps'], data)) > 0,
    wkf_user_is_interpreter: R.path(['wkf_user_is_interpreter'], data),
    billing: R.path(['billing'], data),
    has_proposals: R.path(['has_proposals'], data),
    is_archived: R.path(['is_archived'], data) || false,
  }
}

export function exportCSV (totalObjects, filtering, reportType) {
  let url = ''
  let query = getFiltering(filtering)

  query = {
    ...query,
    api_format: 'csv',
    page_size: totalObjects,
    is_search: true
  }

  if (reportType === prestationConstants.PRESTATION_REPORT_PHOTO) {
    query = {
      ...query,
      picture_date_gte: query.date_gte,
      picture_date_lte: query.date_lte,
      date_gte: undefined,
      date_lte: undefined,
    }
  } else if (reportType === prestationConstants.PRESTATION_REPORT_CREATION_DATE) {
    query = {
      ...query,
      creation_date_gte: query.date_gte,
      creation_date_lte: query.date_lte,
      date_gte: undefined,
      date_lte: undefined,
    }
  } else if (reportType === prestationConstants.PRESTATION_REPORT_VALIDATION_DATE) {
    query = {
      ...query,
      validation_date_gte: query.date_gte,
      validation_date_lte: query.date_lte,
      date_gte: undefined,
      date_lte: undefined,
    }
  }

  url = REPORT_URLS[reportType] + '?' + qs.stringify(query)

  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_URL + url
  }

  return url
}

export function exportDeliveryNote (prestation) {
  const query = {
    uids: [prestation.uid]
  }

  return Request.getRequest(DELIVERY_NOTE_URL + '?' + qs.stringify(query))
}

export function executeAction(data, prestation, intl) {
  return function (dispatch) {
    return Request.postRequest(WORKFLOW_PRESTATION_URL + '?target=' + prestation.uid, data)
      .then((result) => {
        const prestationStep = result.data.object.step_name
        let message = ''
        if (prestationStep === constants.RESENT_FAILED_PRESTATION_STATUS) {
          message = intl.formatMessage(messages.RESENT_INVITATION_REFUSED, {id: prestation.id})
        } else if (prestationStep === constants.INVITATION_RESENT_PRESTATION_STATUS) {
          message = intl.formatMessage(messages.RESENT_INVITATION, {id: prestation.id})
        }
        else {
            message = intl.formatMessage(messages.EMAIL_SENT, {id: prestation.id})
        }
        dispatch({
          type: headerConstants.SET_NOTIFICATION,
          text: message,
          notifType: headerConstants.NOTIF_SUCCESS,
          id: prestation.id
        })
        return result
      })
  }
}

export function getPrestationModification(token) {
  return Request.getRequest(PRESTATION_MODIFICATION_URL + token + '/')
    .then((result) => {
      return result.data.object
    })
}

export function savePrestationModification(token, data) {
  return Request.postRequest(PRESTATION_MODIFICATION_URL + token + '/', data)
    .catch((error) => {
      throw error.response
    })
}

export function getPrestationEvaluation(uid, token) {
  return Request.getRequest(PRESTATION_EVALUATION_URL + uid + '/' + token + '/')
    .then((result) => {
      return result.data.object
    })
}

export function postPrestationEvaluation(data, uid, token) {
  return Request.postRequest(PRESTATION_EVALUATION_URL + uid + '/' + token + '/', data)
    .catch((error) => {
      throw error.response
    })
}

export function getPrestationValidationDistance(token) {
  return Request.getRequest(PRESTATION_VALIDATION_DISTANCE_URL + token + '/')
    .catch((error) => {
      throw error.response
    })
}

export function postPrestationValidationDistance(token) {
  return Request.postRequest(PRESTATION_VALIDATION_DISTANCE_URL + token + '/', {})
    .catch((error) => {
      throw error.response
    })
}



// WEBPACK FOOTER //
// ./src/actions/ApplicantPrestation.js